// ** Modules
import React from 'react';
import { useRouter } from 'next/router';

// ** UI
import useMediaQuery from '@mui/material/useMediaQuery';
import Box, { type BoxProps } from '@mui/material/Box';
import { styled, useTheme } from '@mui/material/styles';

// ** Components
import CustomAppBar from './navMenu/components/CustomAppBar';
import MobileBottomNav, { type MobileBottomNavItem } from '@/components/common/MobileBottomNav';
import { MegaMenuOverlayConnected } from '@/features/ap07';

// ** Layout
import Footer from '@/components/layout/Footer';
import BackToTopButton from '@/components/layout/BackToTopButton';

// ** Hooks
import { useUserBasePath } from '@/hooks/useUserBasePath';

// ** Styles
import DatePickerWrapper from '@/styles/datePickerWrapper';

// ** Types
import type { HorizontalLayoutProps, LayoutProps } from '@/types/layoutProps';

type HorizontalLayoutPropsInternal = LayoutProps & {
	horizontalLayoutProps: HorizontalLayoutProps;
};

const MainContentWrapper = styled(Box)<BoxProps & { path: string }>({
	flexGrow: 1,
	minWidth: 0,
	display: 'flex',
	minHeight: '100vh',
	flexDirection: 'column',
	background: 'transparent'
});

const ContentWrapper = styled('main')({
	flexGrow: 1,
	width: '100%',
	overflowX: 'clip',
	transition: 'padding .25s ease-in-out'
});

const HorizontalLayout = ({
	hidden,
	settings,
	children,
	scrollToTop,
	currentUser,
	host,
	horizontalLayoutProps,
	footerProps,
	...props
}: HorizontalLayoutPropsInternal) => {
	// Hooks
	const theme = useTheme();
	const router = useRouter();
	const userBasePath = useUserBasePath();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

	// States
	const [navValue, setNavValue] = React.useState(0);
	const [isMegaMenuOpen, setIsMegaMenuOpen] = React.useState(false);

	const handleCloseMegaMenu = React.useCallback(() => setIsMegaMenuOpen(false), []);

	React.useEffect(() => {
		router.events.on('routeChangeStart', handleCloseMegaMenu);
		return () => router.events.off('routeChangeStart', handleCloseMegaMenu);
	}, [router.events, handleCloseMegaMenu]);

	const handleToggleMegaMenu = React.useCallback(() => {
		setIsMegaMenuOpen((open) => !open);
	}, []);

	const navItems = React.useMemo<MobileBottomNavItem[]>(() => {
		const items: MobileBottomNavItem[] = [
			{
				key: 'home',
				ariaLabel: 'Home',
				iconName: 'home',
				onClick: () => router.push('/')
			},
			{
				key: 'menu',
				ariaLabel: 'Menu',
				iconName: 'burger',
				onClick: () => handleToggleMegaMenu()
			},
			{
				key: 'events',
				ariaLabel: 'Events',
				iconName: 'events',
				onClick: () => router.push('/veranstaltungen')
			}
		];

		if (currentUser) {
			const targetDashboard = userBasePath || '/intern/mein-bereich';

			items.push({
				key: 'dashboard',
				ariaLabel: 'Dashboard',
				iconName: 'dashboard',
				onClick: () => router.push(targetDashboard)
			});
		} else {
			items.push({
				key: 'login',
				ariaLabel: 'Login',
				iconName: 'profile',
				onClick: () => router.push('/login')
			});
		}

		return items;
	}, [router, handleToggleMegaMenu, currentUser, userBasePath]);

	const handleMobileNavChange = React.useCallback((nextValue: number) => {
		setNavValue(nextValue);
	}, []);

	// ** Vars
	const { contentWidth } = settings;

	// ** Get current path
	const path = window.location.pathname;

	return (
		<React.Fragment>
			{/* Main Content */}
			<MainContentWrapper path={path}>
				{/* AppBar Component */}
				<CustomAppBar
					appBarContent={horizontalLayoutProps.appBar?.content}
					appBarProps={horizontalLayoutProps.appBar?.componentProps}
					currentUser={currentUser}
					settings={settings}
					path={path}
					host={host}
					onMenuClick={handleToggleMegaMenu}
					{...props}
				/>

				<MegaMenuOverlayConnected
					host={host}
					open={isMegaMenuOpen}
					onClose={handleCloseMegaMenu}
					locale={props.locale}
				/>

				{/* Content */}
				<ContentWrapper
					sx={{
						...(contentWidth === 'boxed' && {
							mx: 'auto',
							'@media (min-width:1440px)': { maxWidth: 1440 },
							'@media (min-width:1200px)': { maxWidth: '100%' }
						})
					}}
				>
					{children}
				</ContentWrapper>

				<BackToTopButton />

				{/* Footer Component */}
				<Footer
					host={host}
					navColumns={footerProps.navColumns}
					contact={footerProps.contact}
					socialLinks={footerProps.socialLinks}
					legalLinks={footerProps.legalLinks}
					copyright={footerProps.copyright}
				/>

				{/* Portal for React Datepicker */}
				<DatePickerWrapper sx={{ zIndex: 11 }}>
					<Box id={React.useId()}></Box>
				</DatePickerWrapper>
			</MainContentWrapper>

			{isMobile ? <MobileBottomNav value={navValue} onChange={handleMobileNavChange} items={navItems} /> : null}
		</React.Fragment>
	);
};

export default HorizontalLayout;
